'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ProductForm, type ProductFormRef } from '@/components/partials/products'
import { useCreateProduct } from '@/hooks'
import { type CreateProductParams } from '@/types/product'
import { FormModeEnum } from '@/enums'

const Create = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const productFormRef = useRef<ProductFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createProduct, isPending: isLoading } = useCreateProduct()

  const handleSubmit = (data: CreateProductParams) => {
    createProduct(data, {
      onSuccess: async newProduct => {
        setIsRedirecting(true)
        queryClient.setQueryData(['product', newProduct.id], newProduct)
        await queryClient.invalidateQueries({ queryKey: ['products'] })
        toast.success(t('created_successfully'))
        router.push(`/products/${newProduct.id}`)
      },
      onError: handleError
    })
  }

  const handleSubmitAndNew = (data: CreateProductParams) => {
    createProduct(data, {
      onSuccess: async newProduct => {
        queryClient.setQueryData(['product', newProduct.id], newProduct)
        await queryClient.invalidateQueries({ queryKey: ['products'] })
        const detailUrl = `/products/${newProduct.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        productFormRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (productFormRef.current) {
        productFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_creating'))
  }

  const handleCancel = () => {
    router.push('/products')
  }

  return (
    <ProductForm
      key={formKey}
      ref={productFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default Create
